import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { log } from 'utils/logger';

// Define the available walkthrough sections
export type WalkthroughSection = 
  | 'dashboard-projects'
  | 'integration'
  | 'scan-using-ana'
  | 'fix-by-ana'
  | 'ask-ana';

interface WalkthroughContextType {
  showWalkthrough: boolean;
  currentStep: number;
  selectedSection: WalkthroughSection | null;
  isTrialExpired: boolean;
  setShowWalkthrough: (show: boolean) => void;
  setCurrentStep: (step: number) => void;
  setSelectedSection: (section: WalkthroughSection | null) => void;
  startWalkthrough: () => void;
  completeWalkthrough: () => void;
  closeWalkthrough: () => void;
  nextStep: () => void;
  previousStep: () => void;
  handleScanHistoryClick: (scanId: number) => void;
}

const WalkthroughContext = createContext<WalkthroughContextType | undefined>(undefined);

export const useWalkthrough = () => {
  const context = useContext(WalkthroughContext);
  if (context === undefined) {
    throw new Error('useWalkthrough must be used within a WalkthroughProvider');
  }
  return context;
};

interface WalkthroughProviderProps {
  children: ReactNode;
}

export const WalkthroughProvider: React.FC<WalkthroughProviderProps> = ({ children }) => {
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSection, setSelectedSection] = useState<WalkthroughSection | null>(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);

  // Check if walkthrough should be shown on initial load
  useEffect(() => {
    const walkthroughCompleted = localStorage.getItem('walkthroughCompleted') === 'true';
    const walkthroughDismissed = localStorage.getItem('walkthroughDismissed') === 'true';
    
    // No frontend trial validation - backend handles it
    if (!walkthroughCompleted && !walkthroughDismissed) {
      // Auto-show walkthrough on first visit
      const firstVisitValue = localStorage.getItem('firstVisit');
      const firstVisit = firstVisitValue === 'true';
      if (firstVisit) {
        localStorage.setItem('firstVisit', 'false');
        // Small delay to ensure app is fully loaded
        setTimeout(() => {
          setShowWalkthrough(true);
        }, 1000);
      }
    }
  }, []);

  const startWalkthrough = () => {
    // No frontend trial validation - backend handles it
    setIsTrialExpired(false);
    setCurrentStep(0);
    setSelectedSection(null);
    setShowWalkthrough(true);
    // Clear dismissed flag when manually starting walkthrough
    localStorage.removeItem('walkthroughDismissed');
  };

  const completeWalkthrough = () => {
    setShowWalkthrough(false);
    setSelectedSection(null);
    setCurrentStep(0);
    localStorage.setItem('walkthroughCompleted', 'true');
  };

  const closeWalkthrough = () => {
    setShowWalkthrough(false);
    setSelectedSection(null);
    setCurrentStep(0);
    // Mark walkthrough as dismissed so it doesn't auto-show again
    localStorage.setItem('walkthroughDismissed', 'true');
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const previousStep = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  // Function to handle scan history item click during walkthrough
  const handleScanHistoryClick = (scanId: number) => {
    // Check if we're in the walkthrough and on the scan history step
    if (showWalkthrough) {
      log('Scan history item clicked during walkthrough:', scanId);
      
      // Store information to resume walkthrough after navigation
      localStorage.setItem('walkthrough-resume', 'true');
      
      // Important: Don't close the walkthrough here, let the navigation happen first
      // The GuidedWalkthrough component will handle the walkthrough resumption
      
      // We're not calling closeWalkthrough() here anymore to avoid interfering with navigation
    }
  };

  const value = {
    showWalkthrough,
    currentStep,
    selectedSection,
    isTrialExpired,
    setShowWalkthrough,
    setCurrentStep,
    setSelectedSection,
    startWalkthrough,
    completeWalkthrough,
    closeWalkthrough,
    nextStep,
    previousStep,
    handleScanHistoryClick,
  };

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
};
